import { useState, useEffect } from 'react';
import { Loader, Database, FileText, Briefcase, MessageSquare } from 'lucide-react';
import { FEATURE_LIST, type TenantFeatures, type TenantStatus } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { InlineError } from '../components/InlineError';

interface StatusData {
  status: string;
  version: string;
  tenant_id: string;
  counts: {
    sources: number;
    files: number;
    jobs: number;
    job_messages: number;
  };
}

const STATUS_BADGE: Record<TenantStatus, { label: string; cls: string }> = {
  trialing: { label: 'Trialing', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  active:   { label: 'Active',   cls: 'bg-state-success/15 text-state-success border-state-success/30' },
  past_due: { label: 'Past Due', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  archived: { label: 'Archived', cls: 'bg-coolslate/10 text-coolslate border-coolslate/30' },
};

const KNOWN_FLAG_LABELS: Record<string, string> = {};

function formatFlagValue(value: unknown): string {
  if (value === true) return 'enabled';
  if (value === false || value === null || value === undefined) return 'disabled';
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(', ');
  }
  return String(value);
}

function SubscriptionPanel() {
  const { status, features } = useWorkspace();
  const badge = STATUS_BADGE[status] ?? STATUS_BADGE.active;

  const knownFlags = Object.values(FEATURE_LIST) as string[];
  const presentFeatures: [string, unknown][] = Object.entries(features as TenantFeatures);
  const knownRows = knownFlags.map(flag => [flag, features[flag]] as [string, unknown]);
  const unknownRows = presentFeatures.filter(([flag]) => !knownFlags.includes(flag));

  return (
    <div className="mb-8 bg-white border border-softgrey rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h4 className="text-sm font-semibold text-graphite">Subscription</h4>
          <p className="text-xs text-coolslate mt-0.5">
            Status and capabilities pushed by the control plane.
          </p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${badge.cls}`}>
          {badge.label}
        </span>
      </div>

      <div className="space-y-1.5">
        {knownRows.map(([flag, value]) => {
          const enabled = value !== undefined && value !== false && value !== null;
          return (
            <div
              key={flag}
              className="flex items-center justify-between gap-4 py-2 px-3 bg-white rounded-md border border-softgrey"
            >
              <div className="min-w-0">
                <p className="text-sm text-graphite">{KNOWN_FLAG_LABELS[flag] ?? flag}</p>
                <p className="text-[11px] font-mono text-coolslate mt-0.5">{flag}</p>
              </div>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 ${
                  enabled
                    ? 'bg-state-success/15 text-state-success'
                    : 'bg-coolslate/10 text-coolslate'
                }`}
              >
                {formatFlagValue(value)}
              </span>
            </div>
          );
        })}

        {unknownRows.length > 0 && (
          <>
            <p className="text-[11px] uppercase tracking-wide text-coolslate mt-4 mb-1">
              Other flags (not acted on by this region)
            </p>
            {unknownRows.map(([flag, value]) => (
              <div
                key={flag}
                className="flex items-center justify-between gap-4 py-2 px-3 bg-white rounded-md border border-softgrey opacity-70"
              >
                <p className="text-[11px] font-mono text-coolslate truncate">{flag}</p>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-coolslate/10 text-coolslate shrink-0">
                  {formatFlagValue(value)}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default function StatusPage() {
  const { authFetch } = useAuth();
  const [statusData, setStatusData] = useState<StatusData | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  useEffect(() => {
    authFetch('/status')
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load status')))
      .then(setStatusData)
      .catch(err => setStatusError(err.message));
  }, []);

  const stats = statusData ? [
    { icon: Database, label: 'Data Sources', value: statusData.counts.sources, color: 'text-brand', bgColor: 'bg-brand/10' },
    { icon: FileText, label: 'Files', value: statusData.counts.files, color: 'text-state-success', bgColor: 'bg-state-success/10' },
    { icon: Briefcase, label: 'Jobs', value: statusData.counts.jobs, color: 'text-signal', bgColor: 'bg-signal/10' },
    { icon: MessageSquare, label: 'Job Messages', value: statusData.counts.job_messages, color: 'text-brand-dark', bgColor: 'bg-brand-dark/10' },
  ] : [];

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey">
        <h3 className="text-lg font-semibold">Status</h3>
        <p className="text-sm text-coolslate mt-1">Service health, inventory, and subscription state.</p>
      </div>

      {statusData && (
        <div className="mb-8 overflow-hidden rounded-lg border border-softgrey">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-softgrey">
                <th scope="row" className="w-48 text-left font-medium text-coolslate bg-offwhite px-4 py-2.5">
                  Tenant ID
                </th>
                <td className="px-4 py-2.5 text-graphite font-mono">{statusData.tenant_id}</td>
              </tr>
              <tr className="border-b border-softgrey">
                <th scope="row" className="w-48 text-left font-medium text-coolslate bg-offwhite px-4 py-2.5">
                  API Status
                </th>
                <td className="px-4 py-2.5 text-graphite">{statusData.status}</td>
              </tr>
              <tr>
                <th scope="row" className="w-48 text-left font-medium text-coolslate bg-offwhite px-4 py-2.5">
                  Version
                </th>
                <td className="px-4 py-2.5 text-graphite font-mono">{statusData.version}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <SubscriptionPanel />

      {statusError ? (
        <InlineError message={statusError} className="mb-8" />
      ) : statusData === null ? (
        <div className="mb-8 flex items-center gap-3 text-coolslate">
          <Loader className="w-5 h-5 animate-spin text-brand" />
          <span className="text-sm">Loading status…</span>
        </div>
      ) : (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white border border-softgrey rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-coolslate mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-graphite">{stat.value.toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
